// SENTINEL: 1_Telemetery - Logging Engine (Observability, Vitals)
// Vitality: Healthy
import { SentinelPhase, SentinelLog, SentinelVitals } from '../types/sentinel';

// Re-export strict types for consumers
export type { SentinelPhase, SentinelLog, SentinelVitals };

// Legacy Types for compatibility
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogComponent = string;

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: LogLevel;
  component: LogComponent;
  message: string;
  data?: any;
}

export interface LoggingConfig {
  maxEntries: number;
  verboseMode: boolean;
  enabledLevels: LogLevel[];
  enabledComponents: LogComponent[];
}

const DEFAULT_CONFIG: LoggingConfig = {
  maxEntries: 1000,
  verboseMode: false,
  enabledLevels: ['debug', 'info', 'warn', 'error'],
  enabledComponents: [] // Empty means all
};

const STORAGE_KEY = 'cc_logging_config';
const LOGS_STORAGE_KEY = 'cc_debug_logs';

class SentinelService {
  private static instance: SentinelService;
  private logs: LogEntry[] = [];
  private config: LoggingConfig = DEFAULT_CONFIG;
  private listeners: Set<(logs: LogEntry[]) => void> = new Set();

  // Vitals State
  private sessionStartTime: number = Date.now();
  private errorCount: number = 0;

  // Persist timers
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private notifyTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingNotify = false;
  private lastNotifyTime = 0;
  private static readonly PERSIST_DEBOUNCE_MS = 500;
  private static readonly NOTIFY_THROTTLE_MS = 16;

  private constructor() {
    this.loadConfig();
    this.loadPersistedLogs();
    this.sessionStartTime = Date.now();
  }

  static getInstance(): SentinelService {
    if (!SentinelService.instance) {
      SentinelService.instance = new SentinelService();
    }
    return SentinelService.instance;
  }

  // --- Configuration ---

  private loadConfig() {
    try {
      if (typeof localStorage === 'undefined') return;
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        this.config = { ...DEFAULT_CONFIG, ...JSON.parse(saved) };
      }
    } catch (e) { }
  }

  private saveConfig() {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config));
    } catch (e) { }
  }

  getConfig(): LoggingConfig {
    return { ...this.config };
  }

  updateConfig(updates: Partial<LoggingConfig>) {
    this.config = { ...this.config, ...updates };
    this.saveConfig();
  }

  // --- Persistence ---

  private loadPersistedLogs() {
    try {
      if (typeof localStorage === 'undefined') return;
      const saved = localStorage.getItem(LOGS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.logs = parsed.map((entry: any) => ({
          ...entry,
          timestamp: new Date(entry.timestamp)
        }));
      }
    } catch (e) { }
  }

  private persistLogs() {
    if (this.persistTimer) clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => {
      try {
        if (typeof localStorage === 'undefined') return;
        const toSave = this.logs.slice(-Math.min(this.config.maxEntries, 100));
        localStorage.setItem(LOGS_STORAGE_KEY, JSON.stringify(toSave));
      } catch (e) { }
    }, SentinelService.PERSIST_DEBOUNCE_MS);
  }

  // --- Core Logging ---

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  log(level: LogLevel, component: LogComponent, message: string, data?: any) {
    // 1. Filter
    if (level === 'debug' && !this.config.verboseMode) return;
    if (this.config.enabledLevels.length > 0 && !this.config.enabledLevels.includes(level)) return;
    if (this.config.enabledComponents.length > 0 && !this.config.enabledComponents.includes(component)) return;

    // 2. Create Entry
    const entry: LogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      level,
      component,
      message,
      data: data !== undefined ? this.sanitizeData(data) : undefined
    };

    // 3. Store
    this.logs.push(entry);
    if (this.logs.length > this.config.maxEntries) {
      this.logs = this.logs.slice(-this.config.maxEntries);
    }

    // 4. Update Vitals
    if (level === 'error') this.errorCount++;

    // 5. Notify & Persist
    this.notifyListeners();
    this.persistLogs();

    // 6. Console Fallback
    const style = `[${component}] ${message}`;
    if (level === 'error') console.error(style, data);
    else if (level === 'warn') console.warn(style, data);
    else if (this.config.verboseMode) console.log(style, data);
  }

  // --- Convenience Methods ---

  debug(component: LogComponent, message: string, data?: any) { this.log('debug', component, message, data); }
  info(component: LogComponent, message: string, data?: any) { this.log('info', component, message, data); }
  warn(component: LogComponent, message: string, data?: any) { this.log('warn', component, message, data); }
  error(component: LogComponent, message: string, data?: any) { this.log('error', component, message, data); }

  // --- Sentinel Vitals ---

  getVitals(): SentinelVitals {
    const durationSec = (Date.now() - this.sessionStartTime) / 1000;

    // Calculate Phase Distribution
    // Note: In strict mode, we'd look for 'phase' property in data. 
    // For legacy logs, we inferred via component mapping or accept it's partial.
    const phaseDist: Record<SentinelPhase, number> = {
      '1_Telemetery': 0, '2_Structured': 0, '3_Performance': 0, '4_Evolutionary': 0,
      '5_Cognitive': 0, '6_Physics': 0, '7_Teleological': 0, '8_Fitness': 0
    };

    // Infer phases from components/messages for now
    this.logs.forEach(l => {
      if (l.component === 'App') phaseDist['1_Telemetery']++;
      else if (l.component === 'IdeaManager') phaseDist['6_Physics']++;
      else if (l.component === 'UnifiedAI') phaseDist['4_Evolutionary']++;
      else phaseDist['2_Structured']++;
    });

    return {
      sessionDuration: durationSec,
      errorRate: this.errorCount / Math.max(1, this.logs.length),
      phaseDistribution: phaseDist,
      genomeHealth: {
        complexityScore: this.logs.length * 0.1, // Mock score
        stateSizeMB: 0, // Calculated in App.tsx
        status: this.errorCount > 10 ? 'CRITICAL' : (this.errorCount > 0 ? 'WARNING' : 'NOMINAL')
      },
      currentEngine: '1_Manifestation' // Default
    };
  }

  // --- Subscribers ---

  subscribe(listener: (logs: LogEntry[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    const now = Date.now();
    if (now - this.lastNotifyTime >= SentinelService.NOTIFY_THROTTLE_MS) {
      this.executeNotify();
    } else if (!this.pendingNotify) {
      this.pendingNotify = true;
      setTimeout(() => this.executeNotify(), SentinelService.NOTIFY_THROTTLE_MS);
    }
  }

  private executeNotify() {
    this.pendingNotify = false;
    this.lastNotifyTime = Date.now();
    const copy = [...this.logs];
    this.listeners.forEach(l => l(copy));
  }

  // --- Export ---

  exportLogs(format: 'text' | 'json' = 'json'): string {
    if (format === 'json') {
      return JSON.stringify(this.logs, null, 2);
    }
    return this.logs.map(l =>
      `[${l.timestamp.toISOString()}] [${l.level.toUpperCase()}] [${l.component}] ${l.message} ${l.data ? JSON.stringify(l.data) : ''}`
    ).join('\n');
  }

  getLogs() { return [...this.logs]; }

  getFilteredLogs(levels?: LogLevel[], components?: string[]) {
    return this.logs.filter(l =>
      (!levels || levels.includes(l.level)) &&
      (!components || components.length === 0 || components.includes(l.component))
    );
  }

  clearLogs() {
    this.logs = [];
    this.errorCount = 0;
    this.notifyListeners();
    try { localStorage.removeItem(LOGS_STORAGE_KEY); } catch (e) { }
  }

  private sanitizeData(data: any): any {
    try {
      return JSON.parse(JSON.stringify(data, (key, value) => {
        if (key.match(/key|token|secret/i)) return '[REDACTED]';
        if (value instanceof Date) return value.toISOString();
        if (typeof value === 'string' && value.length > 500) return value.substring(0, 500) + '...';
        return value;
      }));
    } catch { return '[Data Error]'; }
  }
}

export const logger = SentinelService.getInstance();
export default SentinelService;
